import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
// eslint-disable-next-line no-unused-vars
import { IoArrowBack, IoTrash, IoChatboxOutline, IoAddCircleOutline, IoEyeOutline, IoClose, IoHammer, IoShieldCheckmark, IoMegaphone, IoWarning, IoDocumentTextOutline, IoAdd } from 'react-icons/io5';
import moment from 'moment';
import './MisReportesScreen.css'; // --- Premium CSS ---
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// Helper: Get Icon by Type
const getReporteIcon = (tipo) => {
  const t = (tipo || '').toLowerCase();
  if (t.includes('mantenimiento')) return { icon: <IoHammer />, class: 'maintenance' };
  if (t.includes('seguridad')) return { icon: <IoShieldCheckmark />, class: 'security' };
  if (t.includes('sugerencia')) return { icon: <IoMegaphone />, class: 'suggestion' };
  if (t.includes('incidente') || t.includes('queja')) return { icon: <IoWarning />, class: 'incident' };
  return { icon: <IoDocumentTextOutline />, class: 'other' }; // Default
};

// --- Modal Component (Inline) ---
const ReporteDetailModal = ({ reporte, onClose, onCancel }) => {
  if (!reporte) return null;
  const { icon, class: iconClass } = getReporteIcon(reporte.tipo);
  const puedeCancelar = ['Pendiente', 'En Proceso', 'Abierto'].includes(reporte.estatus);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}><IoClose /></button>

        <div className="modal-header">
          <div className={`category-icon-frame ${iconClass}`} style={{ width: 50, height: 50, fontSize: '1.5rem' }}>
            {icon}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>{reporte.tipo}</h2>
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              {moment(reporte.fechaCreacion?.toDate()).format('D [de] MMMM, YYYY - h:mm a')}
            </span>
          </div>
        </div>

        <div className="modal-body-section">
          <label className="modal-label">Descripción</label>
          <p className="modal-text">{reporte.descripcion}</p>
        </div>

        {reporte.archivoUrl && (
          <div className="modal-body-section">
            <label className="modal-label">Adjunto</label>
            <a href={reporte.archivoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
              Ver archivo adjunto
            </a>
          </div>
        )}

        {/* Admin Response */}
        {reporte.comentariosAdmin ? (
          <div className="modal-body-section">
            <label className="modal-label">Respuesta de Administración</label>
            <div className="admin-response-box">
              <IoChatboxOutline size={24} style={{ color: '#2563eb' }} />
              <div>
                <p style={{ margin: 0, color: '#1e3a8a' }}>{reporte.comentariosAdmin}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-body-section" style={{ textAlign: 'center', opacity: 0.6 }}>
            <p style={{ fontStyle: 'italic' }}>Esperando respuesta...</p>
          </div>
        )}

        {/* Actions Footer in Modal */}
        {puedeCancelar && (
          <div style={{ marginTop: 30, textAlign: 'right', borderTop: '1px solid #e5e7eb', paddingTop: 20 }}>
            <button
              className="form-button small danger"
              style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
              onClick={() => onCancel(reporte.id)}
            >
              <IoTrash /> Cancelar este Reporte
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function MisReportesScreen() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedReporte, setSelectedReporte] = useState(null); // For Modal

  // Custom modal states
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
  const [reporteACancelar, setReporteACancelar] = useState(null);
  const [showSuccessCancelModal, setShowSuccessCancelModal] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    const q = query(
      collection(db, "reportes"),
      where("residenteId", "==", currentUser.uid),
      orderBy("fechaCreacion", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaReportes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReportes(listaReportes);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleCancelarClick = (reporteId) => {
    setSelectedReporte(null);
    setReporteACancelar(reporteId);
    setShowConfirmCancelModal(true);
  };

  const ejecutarCancelarReporte = async () => {
    if (!reporteACancelar) return;
    try {
      const docRef = doc(db, "reportes", reporteACancelar);
      await updateDoc(docRef, { estatus: "Cancelado" });
      setShowConfirmCancelModal(false);
      setReporteACancelar(null);
      setSelectedReporte(null); // Close detail modal
      setShowSuccessCancelModal(true);
    } catch (e) {
      console.error("Error al cancelar: ", e);
      alert("Error al cancelar.");
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/mis-reportes" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Mis Reportes</h1>
                <p>Historial y Estatus</p>
                <button className="muro-hero-action-btn" onClick={() => navigate('/reportar')}>
                  <IoAdd size={20} /> Nuevo
                </button>
              </div>
              <IoWarning className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="mis-reportes-container">

      {loading && <div className="empty-state">Cargando reportes...</div>}

      {!loading && reportes.length === 0 && (
        <div className="empty-state">
          <IoDocumentTextOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
          <p>No has generado ningún reporte.</p>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && reportes.length > 0 && (
        <div className="rep-card-grid">
          {reportes.map(reporte => {
            const { icon, class: iconClass } = getReporteIcon(reporte.tipo);
            const statusClass = ['Pendiente', 'Abierto'].includes(reporte.estatus) ? 'pendiente'
              : ['En Proceso', 'En revisión', 'En revision'].includes(reporte.estatus) ? 'proceso'
                : ['Cancelado'].includes(reporte.estatus) ? 'cancelado' : 'solucionado';

            return (
              <div key={reporte.id} className="rep-card-item" onClick={() => setSelectedReporte(reporte)}>
                <div className={`rep-icon-box ${iconClass}`}>
                  {icon}
                </div>
                <div className="rep-info">
                  <div className="rep-header-row">
                    <span className="rep-type">{reporte.tipo}</span>
                    <span className="rep-date">
                      {reporte.fechaCreacion?.toDate ? moment(reporte.fechaCreacion.toDate()).format('D MMM') : ''}
                    </span>
                  </div>
                  <div className="rep-desc">{reporte.descripcion}</div>
                  <span className={`rep-status ${statusClass}`}>{reporte.estatus}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      {selectedReporte && (
        <ReporteDetailModal
          reporte={selectedReporte}
          onClose={() => setSelectedReporte(null)}
          onCancel={handleCancelarClick}
        />
      )}

      {/* Modal de Confirmación de Cancelación Premium */}
      {showConfirmCancelModal && (
        <div className="modal-overlay confirm-modal-overlay">
          <div className="modal-content confirm-modal-content premium-popup">
            <div className="confirm-icon-wrapper warning-icon-wrapper" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}>
              <IoWarning size={46} className="confirm-icon" />
            </div>
            <h3>¿Cancelar Reporte?</h3>
            <p className="confirm-message">
              ¿Estás seguro de que deseas cancelar este reporte? Esta acción no se puede deshacer.
            </p>
            <div className="confirm-actions">
              <button 
                type="button" 
                className="btn-cancelar" 
                onClick={() => { setShowConfirmCancelModal(false); setReporteACancelar(null); }}
              >
                No, mantener
              </button>
              <button 
                type="button" 
                className="btn-confirmar-entrega" 
                onClick={ejecutarCancelarReporte}
                style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)' }}
              >
                Sí, cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Éxito Premium */}
      {showSuccessCancelModal && (
        <div className="modal-overlay confirm-modal-overlay">
          <div className="modal-content confirm-modal-content premium-popup">
            <div className="confirm-icon-wrapper success-icon-wrapper" style={{ backgroundColor: '#ecfdf5', color: '#10b981' }}>
              <IoShieldCheckmark size={46} className="confirm-icon" />
            </div>
            <h3>Reporte Cancelado</h3>
            <p className="confirm-message">
              El reporte ha sido cancelado con éxito y su estatus ha sido actualizado.
            </p>
            <div className="confirm-actions">
              <button 
                type="button" 
                className="btn-confirmar-entrega" 
                onClick={() => setShowSuccessCancelModal(false)}
                style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', flex: 'none', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}

          </div>
        </div>
        {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
        <KondoBottomNav activeRoute="/mis-reportes" />
      </main>
    </div>
  );
}

export default MisReportesScreen;